import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/lib/ssrSnapshot";
import App from "@/App";

// Blank-page-after-deploy self-heal (2026-03-08, hardened 2026-02).
// Emergent's SPA ingress serves `index.html` at 200 for any missing static
// asset. If a stale service worker cached an old `index.html` referencing a
// bundle hash that no longer exists, the browser downloads HTML for a `.js`
// URL, fails to parse it, and the app boots blank. We catch:
//   • ChunkLoadError / "Loading chunk ... failed" / "Loading CSS chunk"
//   • Any script-parse SyntaxError containing "Unexpected token '<'"
//   • React.lazy() dynamic-import rejections
//   • <script>/<link> onerror events on /static/js/* or /static/css/*
// Then trigger a ONE-SHOT cache-busting hard reload, guarded by a
// sessionStorage flag so we can never loop within the same tab.
(function installBlankPageSelfHeal() {
  const KEY = "rf_chunk_reload_done";
  const forceReload = () => {
    if (sessionStorage.getItem(KEY)) return; // one-shot per tab session
    sessionStorage.setItem(KEY, "1");
    // 1) Ask the SW to bin any stale caches for this origin.
    // 2) Unregister the SW so the new index.html is fetched fresh.
    // 3) Reload with a cache-busting `?v={ts}` param so ingress + browser
    //    fetch a fresh index.html and matching bundle hashes.
    const bust = () => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("v", Date.now().toString());
        window.location.replace(url.toString());
      } catch (_) {
        window.location.reload();
      }
    };
    const clearCaches = "caches" in window
      ? caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))).catch(() => null)
      : Promise.resolve();
    clearCaches.finally(bust);
  };
  const isStaleBundleError = (msg) => {
    if (!msg) return false;
    return (
      msg.includes("Unexpected token '<'") ||
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("ChunkLoadError") ||
      msg.includes("Loading chunk") ||
      msg.includes("Loading CSS chunk")
    );
  };
  window.addEventListener("error", (e) => {
    if (isStaleBundleError(e?.message)) return forceReload();
    // <script src="/static/js/main.OLD.js"> or <link href="/static/css/OLD.css">
    const t = e?.target;
    if (!t) return;
    if (t.tagName === "SCRIPT" && (t.src || "").includes("/static/js/")) forceReload();
    if (t.tagName === "LINK" && (t.href || "").includes("/static/css/")) forceReload();
  }, true);
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    const msg = (reason && (reason.message || reason.name)) || String(reason || "");
    if (reason?.name === "ChunkLoadError" || isStaleBundleError(msg)) forceReload();
  });
})();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
